import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

/**
 * THE ONE READER OF `['effective-brand-kit', ownerId]`.
 *
 * TanStack Query dedupes purely by KEY: whichever `queryFn` runs first decides
 * what the key holds for every other consumer, whatever shape the others
 * expected. Three hooks used this key with two shapes — `usePlatformIntegrations`
 * and `useDeactivatedSources` cached the RPC's raw ROWS, `useAudienceVoice`
 * cached a bare id string — so when the rows array won the race the voice hook
 * read an array as its workspace id, serialised it into
 * `brand_kit_id.eq.[object Object]`, and `engagement_items` answered 400. The
 * "Audience messages by intent" card then vanished on `?platform=linkedin`
 * with nothing on the page saying why.
 *
 * It is fixed here by making the shape a property of ONE definition instead of
 * a convention three files had to keep agreeing on: the cache holds the rows,
 * and the id is derived on the way out. Nothing else may register this key.
 *
 * `get_effective_brand_kit` is the zero-arg, auth.uid()-scoped RPC (safe
 * client-side); the actor-taking `get_effective_brand_kit_for` is
 * service_role only.
 */
export const effectiveBrandKitQueryKey = (ownerId: string | null | undefined) =>
  ['effective-brand-kit', ownerId] as const;

type EffectiveBrandKitRow = { id?: string };

export function useEffectiveWorkspaceId({
  ownerId,
  enabled = true,
}: {
  ownerId: string | null | undefined;
  enabled?: boolean;
}): { workspaceId: string | null; isPending: boolean } {
  const { data, isPending } = useQuery({
    queryKey: effectiveBrandKitQueryKey(ownerId),
    queryFn: async (): Promise<EffectiveBrandKitRow[]> => {
      const { data: rows, error } = await supabase.rpc('get_effective_brand_kit');
      if (error) throw new Error(error.message);
      return (rows ?? []) as EffectiveBrandKitRow[];
    },
    enabled: enabled && Boolean(ownerId),
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  return { workspaceId: data?.[0]?.id ?? null, isPending };
}
